import { useEffect, useState } from 'react';
import { getProductLicensing } from '../../lib/licensing/productLicensing';
import { SoundPreview } from './SoundPreview';
import { useCardPrice } from '../../hooks/useCardPrice';
import { confirmedFree, publicDescription } from '../../lib/products/publicProduct';
import { ProductPrices } from './ProductPrices';
import { Link } from 'react-router-dom';
import { resolveStorageUrl } from '../../lib/uploads/resolveStorageUrl';
import {
  getNativeDownloadCount,
  listingTypeLabel,
  productTypeLabel,
  shouldShowNativeDownloadCount,
} from '../../lib/products/productModel';

/**
 * Product card used by shop and admin product listings.
 * @param {{product: any}} props
 */
export function ProductCard({ product, showInternal = false }) {
  const isExternal =
    product.listingType === 'legacyExternal' ||
    product.listingType === 'externalMarketplace' ||
    product.listingType === 'linkOnly';
  const licensing = getProductLicensing(product);
  const { price: cardPrice, loading: loadingCardPrice } = useCardPrice(product);
  const showNativeDownloadCount = shouldShowNativeDownloadCount(product);
  const nativeDownloadCount = getNativeDownloadCount(product);
  const [coverImageUrl, setCoverImageUrl] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function loadCoverImage() {
      const resolvedUrl = await resolveStorageUrl(product?.coverImagePath || '');
      if (mounted) {
        setCoverImageUrl(resolvedUrl);
      }
    }

    loadCoverImage();
    return () => {
      mounted = false;
    };
  }, [product?.coverImagePath]);

  return (
    <article className="card product-card">
      {coverImageUrl ? (
        <img className="product-card-image" src={coverImageUrl} alt={product.title}
          width="512" height="512" loading="lazy" decoding="async" />
      ) : product.coverImagePath ? (
        <div className="product-card-image" aria-hidden="true" />
      ) : null}
      <div className="badge-row">
        <span className="pill product-type">{productTypeLabel(product.productType)}</span>
        {showInternal ? <span className="pill">{listingTypeLabel(product.listingType)}</span> :
          isExternal ? <span className="pill external">External store</span> : null}
        {confirmedFree(product) ? <span className="pill free">Free</span> : null}
      </div>
      <h3>{product.title}</h3>
      <SoundPreview product={product} />
      <p className="product-card-description">{showInternal ? product.description : publicDescription(product).split(/\n\s*\n/)[0]}</p>
      <ProductPrices product={product} cardPrice={cardPrice} loadingCardPrice={loadingCardPrice} compact />
      {licensing.status === 'approved' ? <p className="card-license-note"><a className="text-link" href={licensing.url}>Production-use license {licensing.version}</a>.
        {licensing.attributionRequired ? ' Author credit and Audio at Play link required.' : ' Attribution optional.'}</p> : null}
      {licensing.proposal?.attributionRequired ? <p className="card-license-note">
        Proposed free license: author credit and audioatplay.ca link required. <a className="text-link" href={licensing.proposal.reviewUrl}>Awaiting approval</a>.
      </p> : null}
      <div className="chip-row">
        {(product.tags || []).map((tag) => (
          <span className="chip" key={tag}>
            #{tag}
          </span>
        ))}
      </div>
      <div className="meta-row">
        <div className="meta-stack">
          {showInternal ? <span className="mono">{product.slug}</span> : null}
          {showInternal && showNativeDownloadCount ? (
            <span className="mono product-download-count">Native downloads: {nativeDownloadCount}</span>
          ) : null}
        </div>
        <Link className="btn btn-ghost product-card-action" to={`/product/${product.slug}`} aria-label={'View ' + product.title}>
          View product
        </Link>
      </div>
    </article>
  );
}
